import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Users } from 'lucide-react';
import type { Classroom } from '../../../accounts';
import { useClickOutside } from '../../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { CoBadge } from './CoBadge';
interface ClassroomSwitcherProps {
  classrooms: Classroom[];
  selectedClassroomId: string | null;
  onSelectClassroom: (id: string | null) => void;
  /** Show a label like "Klas" before the value. Default: true */
  showLabel?: boolean;
  /** Visual size. Default: 'sm' */
  size?: 'sm' | 'md';
  /** Hide entirely when there's only one classroom (just shows the name as static label). Default: true */
  hideWhenSingle?: boolean;
  /** Active teacher account id — used to mark co-teacher classrooms with a 'Co' badge. */
  activeTeacherId?: string;
}
/**
 * Page-header classroom selector. Mirrors the sidebar dropdown but lives
 * inline next to a page title so teachers on tablet/mobile can switch
 * classrooms without opening the drawer.
 *
 * No Djay — this is operational UI (Zone 2 NO per Djay framework).
 */
export function ClassroomSwitcher({
  classrooms,
  selectedClassroomId,
  onSelectClassroom,
  showLabel = true,
  size = 'sm',
  hideWhenSingle = true,
  activeTeacherId
}: ClassroomSwitcherProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => {
    if (open) setOpen(false);
  });
  useEscapeKey(() => {
    if (open) setOpen(false);
  });
  if (classrooms.length === 0) return null;
  const active = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const display = active ? active.name : 'Alle klassen';
  const learnerCount = active ? active.learnerIds.length : null;
  const isCoTeach = (c: Classroom) => !!activeTeacherId && c.teacherId !== activeTeacherId;
  // Static label fallback for single-classroom teachers
  if (hideWhenSingle && classrooms.length === 1) {
    const only = classrooms[0];
    return <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-600" data-id="element-7965">
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-7966" />
        <span data-id="element-7967">{only.name}</span>
        {isCoTeach(only) && <CoBadge data-id="element-7968" />}
        <span className="text-slate-400 font-medium" data-id="element-7969">
          · {only.learnerIds.length}
        </span>
      </div>;
  }
  const buttonSize = size === 'md' ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-xs';
  return <div ref={containerRef} className="relative inline-block" data-id="element-7970">
      <button type="button" onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} className={`${buttonSize} inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 shadow-sm`} data-id="element-7971">
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-7972" />
        {showLabel && <span className="text-slate-400 font-medium" data-id="element-7973">Klas:</span>}
        <span className="truncate max-w-[160px] text-slate-900" data-id="element-7974">{display}</span>
        {active && isCoTeach(active) && <CoBadge data-id="element-7975" />}
        {learnerCount !== null && <span className="text-slate-400 font-medium" data-id="element-7976">· {learnerCount}</span>}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-7977" />
      </button>

      <AnimatePresence data-id="element-7978">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-30 overflow-hidden py-1.5" role="listbox" aria-label="Klas selecteren" data-id="element-7979">
            <button onClick={() => {
          onSelectClassroom(null);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === null} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-7980">
              <span data-id="element-7981">Alle klassen</span>
              {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-7982" />}
            </button>
            <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-7983" />
            {classrooms.map(c => <button key={c.id} onClick={() => {
          onSelectClassroom(c.id);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === c.id} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-7984">
                <div className="flex items-center gap-2 truncate min-w-0" data-id="element-7985">
                  <span className="truncate" data-id="element-7986">{c.name}</span>
                  {isCoTeach(c) && <CoBadge data-id="element-7987" />}
                  <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold flex-shrink-0" data-id="element-7988">
                    {c.learnerIds.length}
                  </span>
                </div>
                {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-7989" />}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}